import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getAllGames } from '@/logic/games/all-games';
import { type Game } from '@/types/games';
import { searchGames, type SearchOptions } from '@/lib/search';
import { mapSearchHitsToGames } from '@/lib/resolve-search-hit';
import { isMlSearchActive } from '@/lib/ml-search-gate';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useStore } from '@/store/store';
import { LOCALES, SEARCH } from '@/utils/constants';
import type { Locale } from '@/types/constants';
import { ddCancelVital, ddRumAction } from '@/utils/datadog';
import { SEARCH_VITALS } from '@/utils/datadog/vitals';
import { getSnowplowSessionId } from '@/tracking/snowplow/utils';
import { AMPLITUDE_EXPERIMENTS, isExperimentKeyOn } from '@/utils/experiments';
/**
 * Context used to share state and logic for the search drawer components
 */

interface SearchContextType {
    query?: string;
    setQuery: (query: string) => void;
    clearQuery: () => void;
    foundGames: Game[];
    allGames: Game[];
    initializeGames: () => Promise<void>;
    isLoading: boolean;
    /** Whether more results are available from the API. */
    hasMore: boolean;
    /** Whether the next page is currently loading. */
    isLoadingMore: boolean;
    /** Load the next page of search results (for infinite scroll). */
    fetchNextPage: () => Promise<void>;
    searchEngineVersion: string | null;
    /** True when ML search throttle is on and the user is in the search-ml variant. */
    mlSearchActive: boolean;
    /**
     * Set when the latest search request for `query` received a non-null SearchHits response
     * (including zero hits). Left null on network failure, rejected session lookup, or null API
     * response. `isSearching === false` cannot be used instead: it is also false before a search
     * starts, and a zero-result search leaves `foundGames` untouched.
     */
    completedSearchQuery: string | null;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

interface SearchProviderProps {
    children: ReactNode;
}

const buildSearchGamesOptions = (
    base: Pick<SearchOptions, 'pageIndex' | 'pageSize'>,
    ml: { experimentGroupId: string; sessionId: string; hasCasinoSearchService: boolean }
): SearchOptions => ({
    ...base,
    experimentGroupId: ml.experimentGroupId,
    sessionId: ml.sessionId,
    requestId: crypto.randomUUID(),
    hasCasinoSearchService: ml.hasCasinoSearchService
});

const logDiscardedSearchHits = (
    discardedHits: ReturnType<typeof mapSearchHitsToGames>['discardedHits'],
    context: { query: string; mlSearchActive: boolean; catalogSize: number; totalHits: number }
): void => {
    if (discardedHits.length === 0) {
        return;
    }

    ddRumAction({
        name: 'SEARCH_HIT_DISCARDED',
        description: 'Search API returned hits that could not be resolved to games',
        use_case: 'GLOBAL',
        context: {
            query: context.query,
            ml_search_active: context.mlSearchActive,
            catalog_size: context.catalogSize,
            total_hits: context.totalHits,
            discarded_count: discardedHits.length,
            discarded_hit_ids: discardedHits.map((hit) => hit.hitId),
            discarded_hits: discardedHits
        }
    });
};

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
    const [query, setQuery] = useState<string | undefined>();
    const [allGames, setAllGames] = useState<Map<string, Game>>(new Map<string, Game>());
    const [foundGames, setFoundGames] = useState<Game[]>([]);
    const [searchEngineVersion, setSearchEngineVersion] = useState<string | null>(null);
    const [searchPageIndex, setSearchPageIndex] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [completedSearchQuery, setCompletedSearchQuery] = useState<string | null>(null);
    const { region, isMobile, isTablet, experiments } = useGlobalContext();
    const searchMlExperimentValue = useStore((s) => s.search.searchMlExperimentValue) ?? '';

    const initializeGamesGenerationRef = useRef(0);
    const queryRef = useRef<string | undefined>(undefined);
    queryRef.current = query;

    const isLoading = useMemo(() => isInitializing || isSearching, [isInitializing, isSearching]);

    const prismicLocale = LOCALES[region as Locale];
    const pageSize = isMobile || isTablet ? SEARCH.PAGE_SIZE_MOBILE : SEARCH.PAGE_SIZE_DESKTOP;
    const hasCasinoSearchService = isExperimentKeyOn(experiments, AMPLITUDE_EXPERIMENTS.HAS_CASINO_SEARCH_SERVICE);
    const mlSearchActive = isMlSearchActive(hasCasinoSearchService, searchMlExperimentValue);

    const initializeGames = useCallback(async () => {
        const generation = ++initializeGamesGenerationRef.current;

        setIsInitializing(true);
        try {
            const response = await getAllGames();
            if (generation !== initializeGamesGenerationRef.current) {
                return;
            }
            if (response.length > 0) {
                setAllGames(new Map<string, Game>(response.map((game: Game) => [game.uid, game])));
            } else {
                setAllGames(new Map<string, Game>());
            }
        } finally {
            if (generation === initializeGamesGenerationRef.current) {
                setIsInitializing(false);
            }
        }
    }, []);

    const filterGames = useCallback(
        async (searchQuery: string) => {
            try {
                const sessionId = (await getSnowplowSessionId()) ?? '';
                const searchHits = await searchGames(
                    searchQuery,
                    prismicLocale,
                    buildSearchGamesOptions(
                        { pageIndex: 0, pageSize },
                        {
                            experimentGroupId: searchMlExperimentValue,
                            sessionId,
                            hasCasinoSearchService
                        }
                    )
                );
                if (searchQuery !== queryRef.current) {
                    return;
                }

                if (!searchHits) {
                    setSearchEngineVersion(null);
                    setFoundGames([]);
                    setHasMore(false);
                    setSearchPageIndex(0);
                    ddCancelVital(SEARCH_VITALS.QUERY_RESPONSE);
                    return;
                }

                setSearchEngineVersion(searchHits.searchEngineVersion);

                const { games: matchedGames, discardedHits } = mapSearchHitsToGames(searchHits.hits, allGames);
                logDiscardedSearchHits(discardedHits, {
                    query: searchQuery,
                    mlSearchActive,
                    catalogSize: allGames.size,
                    totalHits: searchHits.total.value
                });

                setFoundGames(matchedGames);
                setHasMore(searchHits.hasNextPage);
                setSearchPageIndex(1);
                setCompletedSearchQuery(searchQuery);
            } catch {
                if (searchQuery !== queryRef.current) {
                    return;
                }

                setFoundGames([]);
                setHasMore(false);
                setSearchPageIndex(0);
                ddCancelVital(SEARCH_VITALS.QUERY_RESPONSE);
            } finally {
                if (searchQuery === queryRef.current) {
                    setIsSearching(false);
                }
            }
        },
        [allGames, prismicLocale, pageSize, searchMlExperimentValue, hasCasinoSearchService, mlSearchActive]
    );

    const fetchNextPage = useCallback(async () => {
        if (!query || query.length < SEARCH.MIN_QUERY_LENGTH || !hasMore || isLoadingMore) {
            return;
        }
        setIsLoadingMore(true);
        try {
            const sessionId = (await getSnowplowSessionId()) ?? '';
            const searchHits = await searchGames(
                query,
                prismicLocale,
                buildSearchGamesOptions(
                    { pageIndex: searchPageIndex, pageSize },
                    {
                        experimentGroupId: searchMlExperimentValue,
                        sessionId,
                        hasCasinoSearchService
                    }
                )
            );

            if (query !== queryRef.current) {
                return;
            }
            if (!searchHits) {
                setHasMore(false);
                return;
            }

            setSearchEngineVersion(searchHits.searchEngineVersion);

            const { games: matchedGames, discardedHits } = mapSearchHitsToGames(searchHits.hits, allGames);
            logDiscardedSearchHits(discardedHits, {
                query,
                mlSearchActive,
                catalogSize: allGames.size,
                totalHits: searchHits.hits.length
            });
            setFoundGames((prev) => [...prev, ...matchedGames]);
            setHasMore(searchHits.hasNextPage);
            setSearchPageIndex((prev) => prev + 1);
        } catch {
            if (query !== queryRef.current) {
                return;
            }
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
        }
    }, [
        query,
        prismicLocale,
        pageSize,
        searchPageIndex,
        hasMore,
        isLoadingMore,
        allGames,
        searchMlExperimentValue,
        hasCasinoSearchService,
        mlSearchActive
    ]);

    const clearQuery = useCallback(() => {
        setQuery('');
        setFoundGames([]);
        setSearchPageIndex(0);
        setHasMore(false);
        setIsSearching(false);
        setSearchEngineVersion(null);
        setCompletedSearchQuery(null);
    }, []);

    useEffect(() => {
        if (query && query.length >= SEARCH.MIN_QUERY_LENGTH) {
            setCompletedSearchQuery(null);
            setIsSearching(true);
            if (!mlSearchActive && allGames.size === 0 && isInitializing) {
                return;
            }
            // Fire-and-forget: effect must not return a Promise; void satisfies no-floating-promises.
            void filterGames(query);
        } else {
            setIsSearching(false);
            setCompletedSearchQuery(null);
            setFoundGames([]);
            setHasMore(false);
            setSearchPageIndex(0);
            setSearchEngineVersion(null);
        }
    }, [query, filterGames, allGames, isInitializing, mlSearchActive]);

    const allGamesArray = useMemo(() => Array.from(allGames.values()), [allGames]);

    return (
        <SearchContext.Provider
            value={{
                query,
                setQuery,
                clearQuery,
                foundGames,
                allGames: allGamesArray,
                initializeGames,
                isLoading,
                hasMore,
                isLoadingMore,
                fetchNextPage,
                searchEngineVersion,
                mlSearchActive,
                completedSearchQuery
            }}>
            {children}
        </SearchContext.Provider>
    );
};

export const useSearchContext = (): SearchContextType => {
    const context = useContext(SearchContext);
    if (!context) {
        throw new Error('useSearchContext must be used within an SearchProvider');
    }
    return context;
};
